import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JsonLd from "@/components/JsonLd";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SITE_URL, scrollToId } from "@/lib/api";

const PLASTERS = [
  {
    key: "mineralna", name: "Minerálna", vapor: "Veľmi vysoká", water: "Stredná", dirt: "Nižšia (odporúča sa náter)",
    color: "Obmedzená (často treba náter)", life: "krátka–stredná", price: "€ (najnižšia)", priceM2: "~3 – 6 €/m²",
    use: "Minerálna vata, veľké a dotačné projekty, ekonomické riešenie.",
    pros: ["Najnižšia cena", "Nehorľavá, prírodná", "Veľmi vysoká paropriepustnosť"],
    cons: ["Obmedzené farby (často náter navyše)", "Náchylnejšia na trhliny a riasy", "Rýchlo tuhne — náročná montáž"],
  },
  {
    key: "akrylatova", name: "Akrylátová", vapor: "Nízka", water: "Vysoká (hydrofóbna)", dirt: "Nižšia (priťahuje prach)",
    color: "Výborná — sýte farby", life: "stredná", price: "€ (nízka)", priceM2: "~5 – 9 €/m²",
    use: "EPS fasády, chránené a nižšie fasády. Nevhodná na minerálnu vatu.",
    pros: ["Sýte a stále farby", "Odolná voči vlhkosti", "Pružná — odolná voči trhlinám"],
    cons: ["Nízka paropriepustnosť", "Priťahuje prach (elektrostaticky)", "Nevhodná na minerálnu vatu"],
  },
  {
    key: "silikatova", name: "Silikátová", vapor: "Veľmi vysoká", water: "Dobrá", dirt: "Dobrá (alkalická, bráni riasam)",
    color: "Minerálny vzhľad, menej sýtych odtieňov", life: "dlhá", price: "€€ (stredná)", priceM2: "~7 – 11 €/m²",
    use: "Minerálna vata, difúzne otvorené konštrukcie, pamiatky, murivo.",
    pros: ["Veľmi vysoká paropriepustnosť", "Prirodzene bráni riasam a plesniam", "Dlhá životnosť"],
    cons: ["Obmedzenejšia paleta farieb", "Vyššia cena než akrylát", "Náročnejšia aplikácia"],
  },
  {
    key: "silikon-silikatova", name: "Silikón-silikátová", vapor: "Vysoká", water: "Vysoká", dirt: "Veľmi dobrá",
    color: "Široká paleta", life: "dlhá", price: "€€€ (vyššia)", priceM2: "~8 – 12 €/m²",
    use: "Univerzálna voľba — kombinuje výhody silikátu a silikónu.",
    pros: ["Vysoká paropriepustnosť aj hydrofóbnosť", "Dobrý samočistiaci efekt", "Široká paleta farieb"],
    cons: ["Vyššia cena", "Kvalita sa líši podľa výrobcu"],
  },
  {
    key: "silikonova", name: "Silikónová", vapor: "Vysoká", water: "Najvyššia (samočistiaca)", dirt: "Výborná (voda odplavuje nečistoty)",
    color: "Výborná — široká paleta, stále farby", life: "najdlhšia", price: "€€€€ (najvyššia)", priceM2: "~10 – 15 €/m²",
    use: "Prémiové fasády, exponované a vlhké lokality, dlhá životnosť.",
    pros: ["Najlepší samočistiaci efekt", "Výborná odolnosť voči riasam a plesniam", "Dlhá životnosť a stále farby"],
    cons: ["Najvyššia cena", "Vyššia počiatočná investícia"],
  },
];

const ROWS = [
  { label: "Paropriepustnosť", key: "vapor" },
  { label: "Odolnosť voči vlhkosti", key: "water" },
  { label: "Odolnosť voči špine a riasam", key: "dirt" },
  { label: "Farebná stálosť / paleta", key: "color" },
  { label: "Cena (materiál, orientačne)", key: "priceM2" },
  { label: "Vhodné použitie", key: "use" },
];

const STRUCTURES = [
  { name: "Ryhovaná (škrabaná)", desc: "Najčastejšia štruktúra. Zrno pri sťahovaní vytvára ryhy — vzhľad určuje smer ťahu (zvislý, vodorovný, kruhový) a zrnitosť." },
  { name: "Zatieraná (roztieraná)", desc: "Jemnejší, bodkovaný povrch bez výrazných rýh. Modernejší, hladší vzhľad." },
  { name: "Mozaiková (marmolit)", desc: "Farebné kamienky v transparentnom spojive — odolná a umývateľná, používa sa najmä na sokel." },
];

const GRAINS = [
  { size: "1,0 – 1,5 mm", note: "jemná štruktúra, nižšia spotreba" },
  { size: "2,0 mm", note: "najčastejšia voľba pre rodinné domy" },
  { size: "2,5 – 3,0 mm", note: "hrubšia štruktúra, vyššia spotreba materiálu" },
];

const FAQ = [
  { q: "Ktorá fasádna omietka je najlepšia?", a: "Neexistuje jedna univerzálne najlepšia — závisí od podkladu a priorít. Silikónová a silikón-silikátová sú kvalitatívne najvyššie (samočistiaci efekt, odolnosť voči riasam, dlhá životnosť), ale najdrahšie. Akrylátová je cenovo dostupná so sýtymi farbami, no s nižšou paropriepustnosťou. Na minerálnu vatu sa hodí silikátová alebo minerálna omietka." },
  { q: "Akú omietku na minerálnu vatu a akú na polystyrén?", a: "Na minerálnu vatu volte paropriepustnú omietku — silikátovú, silikón-silikátovú alebo minerálnu. Na polystyrén (EPS) vyhovie akrylátová, silikónová aj silikón-silikátová. Akrylátovú nedávajte na minerálnu vatu kvôli nízkej paropriepustnosti." },
  { q: "Aký je rozdiel medzi silikónovou a akrylátovou omietkou?", a: "Silikónová je paropriepustná, má výborný samočistiaci efekt a odolnosť voči riasam, ale je drahšia. Akrylátová je lacnejšia, s výbornou farebnou stálosťou, no má nízku paropriepustnosť a viac priťahuje prach." },
  { q: "Akú zrnitosť omietky zvoliť?", a: "Najčastejšou voľbou pre rodinné domy je zrnitosť 2,0 mm. Jemnejšie zrno (1,5 mm) pôsobí hladšie a má nižšiu spotrebu, hrubšie zrno (2,5–3,0 mm) vytvára výraznejšiu štruktúru, ale spotrebuje viac materiálu." },
];

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function PlasterCompare() {
  useEffect(() => {
    document.title = "Porovnanie fasádnych omietok: akrylátová, silikátová, silikónová — Zateplenia.eu";
    setMeta("description", "Porovnanie fasádnych omietok — akrylátová, silikátová, silikón-silikátová, silikónová a minerálna. Výhody, nevýhody, ceny, paropriepustnosť aj štruktúry a zrnitosti omietok.");
    window.scrollTo(0, 0);
  }, []);

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE_URL}/omietky#faq`,
    "mainEntity": FAQ.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
  };

  return (
    <div className="bg-ink min-h-screen">
      <Header />
      <JsonLd id="ld-plasters" data={faqLd} />
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 pt-32 pb-24" data-testid="plasters-page">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Porovnanie omietok</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] text-white max-w-4xl">
          Akú fasádnu <span className="text-gold">omietku</span> zvoliť?
        </h1>
        <p className="text-white/55 text-sm md:text-base mt-5 max-w-2xl">
          Prehľadné porovnanie 5 typov fasádnych omietok — akrylátová, silikátová, silikón-silikátová,
          silikónová a minerálna. Pozrite si výhody, nevýhody, ceny, paropriepustnosť aj štruktúry a zrnitosti.
        </p>

        {/* Comparison table */}
        <div className="mt-12 overflow-x-auto border border-white/10" data-testid="plasters-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-ink-2">
                <th className="text-left px-5 py-4 font-mono2 text-xs uppercase tracking-widest text-white/40 w-56">Vlastnosť</th>
                {PLASTERS.map((m) => (
                  <th key={m.key} className="text-left px-4 py-4 text-white font-semibold border-l border-white/10">{m.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.key} className="border-t border-white/8">
                  <td className="px-5 py-4 text-white/60 align-top">{row.label}</td>
                  {PLASTERS.map((m) => (
                    <td key={m.key} className="px-4 py-4 text-white/80 border-l border-white/10 align-top">{m[row.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pros / cons cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {PLASTERS.map((m) => (
            <div key={m.key} className="border border-white/10 bg-ink-2 p-6" data-testid={`plaster-card-${m.key}`}>
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-white font-semibold text-base">{m.name}</h3>
                <span className="text-gold/80 font-mono2 text-xs">{m.price}</span>
              </div>
              <ul className="space-y-2 mb-4">
                {m.pros.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-white/70 text-sm"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {p}</li>
                ))}
              </ul>
              <ul className="space-y-2">
                {m.cons.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-white/50 text-sm"><X className="w-4 h-4 text-red-400/70 shrink-0 mt-0.5" /> {c}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Structures */}
        <h2 className="font-display text-3xl md:text-4xl text-white mt-16 mb-2">Štruktúry a zrnitosti omietok</h2>
        <p className="text-white/50 text-sm mb-8 max-w-2xl">Vzhľad fasády okrem farby určuje aj štruktúra a zrnitosť omietky.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STRUCTURES.map((s) => (
            <div key={s.name} className="border border-white/10 bg-ink-2 p-6" data-testid={`structure-${s.name}`}>
              <h3 className="text-white font-semibold mb-2">{s.name}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          {GRAINS.map((g) => (
            <div key={g.size} className="border border-gold/20 bg-gold/[0.04] p-5" data-testid={`grain-${g.size}`}>
              <div className="font-display text-2xl text-gold">{g.size}</div>
              <div className="text-white/55 text-sm mt-1">{g.note}</div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <h2 className="font-display text-3xl md:text-4xl text-white mt-16 mb-6">Časté otázky o omietkach</h2>
        <Accordion type="single" collapsible className="max-w-3xl" data-testid="plasters-faq">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`p-${i}`} className="border-white/10" data-testid={`plasters-faq-${i}`}>
              <AccordionTrigger className="text-white font-semibold text-base md:text-lg text-left hover:no-underline hover:text-gold [&[data-state=open]]:text-gold py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-white/60 text-sm md:text-base leading-relaxed pr-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Related */}
        <div className="mt-14">
          <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-4">Súvisiace</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/materialy" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Porovnanie izolačných materiálov</Link>
            <Link to="/blog/typy-a-struktury-fasadnych-omietok" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Typy a štruktúry omietok</Link>
            <Link to="/blog/akym-farbam-sa-vyvarovat-na-fasade" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Akým farbám sa vyvarovať</Link>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 border border-gold/30 bg-gold/5 p-8 md:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="plasters-cta">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-white">Naplánujte si zateplenie s omietkou</h2>
            <p className="text-white/55 text-sm mt-2">Vypočítajte cenu kompletného systému aj s finálnou omietkou.</p>
          </div>
          <Link to="/#kalkulacka" onClick={() => setTimeout(() => scrollToId("#kalkulacka"), 60)} data-testid="plasters-cta-btn"
            className="inline-flex items-center justify-center gap-2 bg-gold text-ink px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors whitespace-nowrap">
            Vypočítať cenu <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
